<template>
  <AutoScrollStrip
    v-if="hasEnoughCompanies"
    class="header-companies"
    :item-key="(c) => c.companyCard.id"
    :items="companies"
    :label="tr('main_site.product.header_companies_label')"
  >
    <template #default="{ item: company }">
      <HeaderCompanyItem :company="company" />
    </template>
  </AutoScrollStrip>
  <div
    v-else-if="companies.length"
    class="header-companies header-companies--static"
  >
    <div class="header-companies__track">
      <HeaderCompanyItem
        v-for="company in companies"
        :key="company.companyCard.id"
        :company="company"
      />
    </div>
  </div>
  <HeaderCompaniesSkeleton v-else-if="isLoading" />
</template>

<script setup lang="ts">
import { useQuery } from "@pinia/colada";
import { computed, toRef } from "vue";

import {
  injectI18nLocale,
  useI18nMainSiteProduct,
} from "@solvari/translations";

import { useCompanyCards } from "~/features/company-wall/api/localSearch.api.ts";
import AutoScrollStrip from "~/shared/components/AutoScrollStrip.vue";

import HeaderCompaniesSkeleton from "./HeaderCompaniesSkeleton.vue";
import HeaderCompanyItem from "./HeaderCompanyItem.vue";

const props = withDefaults(
  defineProps<{
    geoProductSlug: string;
    minCompanies?: number;
  }>(),
  { minCompanies: 5 },
);

const localeCode = injectI18nLocale().value;
const { tr } = useI18nMainSiteProduct();

const { data, isLoading } = useQuery(
  useCompanyCards({
    geoProductCategorySlug: props.geoProductSlug,
    perPage: 8,
    minReviews: 1,
    localeCode,
    withProfileAvatar: true,
  }),
);
const companies = computed(() => data.value?.data ?? []);
const hasEnoughCompanies = computed(
  () => companies.value.length >= props.minCompanies,
);
</script>

<style lang="postcss">
.header-companies {
  &__item {
    @apply flex min-h-12 w-48 items-center rounded-md bg-primary-100 p-1 text-slate-700 gap-2;
  }

  &__logo {
    @apply flex h-10 w-10 shrink-0 items-center justify-center rounded bg-slate-100;

    &__image {
      @apply h-full w-full rounded object-contain;
    }

    &__placeholder {
      @apply text-sm text-slate-400;
    }
  }

  &__name {
    @apply line-clamp-2 grow text-sm font-semibold text-slate-700;
  }

  &__rating {
    @apply shrink-0 self-start;
  }

  &__track {
    @apply flex gap-2;
  }
}
</style>
